import type { Release, ReleaseYear } from "./types";

/**
 * Groups releases by calendar year, newest year and newest month first.
 *
 * @param releases - Releases in any order.
 * @returns One entry per year that has releases.
 */
export function groupReleasesByYear(releases: Release[]): ReleaseYear[] {
  const sorted = [...releases].sort((a, b) => b.month.localeCompare(a.month));
  const years: ReleaseYear[] = [];
  for (const release of sorted) {
    const year = release.month.slice(0, 4);
    const last = years.at(-1);
    if (last?.year === year) {
      last.releases.push(release);
      continue;
    }
    years.push({ year, releases: [release] });
  }
  return years;
}

/**
 * Formats a `YYYY-MM` month for display.
 *
 * @param month - Calendar month as `YYYY-MM`.
 * @param style - `long` for "October 2026", `month` for "October".
 * @returns The formatted month, in UTC so server and client render the same text.
 */
export function formatReleaseMonth(month: string, style: "long" | "month" = "long"): string {
  const [year, monthIndex] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, monthIndex - 1, 1));
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: style === "long" ? "numeric" : undefined,
    timeZone: "UTC",
  });
}
